import { FC } from 'react';

import { useRecoilValue } from 'recoil';

import {
  giftOrderForAdminDataAtomFamily,
  giftOrderForAdminStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import { useGiftOrderForAdminHook } from '@shared/api/giftOrder/giftOrder.hook';
import { GiftCouponSummary } from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';

import { CouponDetailModal } from './CouponDetailModal';

interface OrderCancelLauncherProps {
  /** 취소 처리할 주문 */
  orderId: string;
  /** 열자마자 취소 대상으로 체크해 둘 쿠폰 (선물거절 건) */
  couponId?: string;
  onClose: () => void;
}

/**
 * 발송대기 페이지에서 **기존 주문 쿠폰 상세 모달을 그대로 재사용**하기 위한 얇은 로더.
 *
 * 취소 금액 계산(부분/전체 판정, 배송비 흡수 보정)과 가상계좌·입금 결제의 수동환불 분기가
 * 이미 그 모달에 다 들어있어, 탭에서 취소 UI를 새로 만들면 그 로직을 이중 관리하게 된다.
 * 그래서 주문만 조회해 같은 모달을 띄우고, 대상 쿠폰만 미리 체크해 준다.
 */
export const OrderCancelLauncher: FC<OrderCancelLauncherProps> = ({
  orderId,
  couponId,
  onClose,
}) => {
  useGiftOrderForAdminHook(orderId);
  const status = useRecoilValue(giftOrderForAdminStatusAtomFamily(orderId));
  const order = useRecoilValue(giftOrderForAdminDataAtomFamily(orderId));

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
        <div className="rounded-xl bg-white px-8 py-6">
          <Loading />
        </div>
      </div>
    );
  }

  if (!order || !order.coupons) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative w-full max-w-sm rounded-xl bg-white p-5 text-center shadow-2xl">
          <p className="text-sm text-gray-600">주문 정보를 불러오지 못했습니다.</p>
          <button
            onClick={onClose}
            className="mt-4 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600"
          >
            닫기
          </button>
        </div>
      </div>
    );
  }

  return (
    <CouponDetailModal
      coupons={order.coupons as GiftCouponSummary[]}
      orderId={order._id}
      order={order}
      initialSelectedCouponIds={couponId ? [couponId] : undefined}
      onClose={onClose}
    />
  );
};
